import Phaser from 'phaser';
import { NetworkService } from '../services/NetworkService';
import { RoomInfo } from '../types/multiplayer';
import { GameSettings } from '../utils/GameSettings';

export class LobbyScene extends Phaser.Scene {
  private networkService: NetworkService = NetworkService.getInstance();
  private roomsList: RoomInfo[] = [];
  private roomButtons: Phaser.GameObjects.Container[] = [];
  private createRoomButton!: Phaser.GameObjects.Text;
  private backButton!: Phaser.GameObjects.Text;
  private refreshButton!: Phaser.GameObjects.Text;
  private titleText!: Phaser.GameObjects.Text;
  private subtitleText!: Phaser.GameObjects.Text;
  private roomNameInput!: Phaser.GameObjects.DOMElement;
  private maxPlayersInput!: Phaser.GameObjects.DOMElement;
  private playerNameInput!: Phaser.GameObjects.DOMElement;
  private errorText!: Phaser.GameObjects.Text;
  private createRoomPanel!: Phaser.GameObjects.Container;
  private loadingText!: Phaser.GameObjects.Text;
  private connectionStatus!: Phaser.GameObjects.Text;
  private buttonStyle: Phaser.Types.GameObjects.Text.TextStyle = {
    color: '#ffffff',
    backgroundColor: '#1a6dd7',
    padding: { left: 15, right: 15, top: 10, bottom: 10 },
    fixedWidth: 200,
    align: 'center',
    fontFamily: 'Arial',
    fontSize: '18px'
  };
  
  private playerName: string = 'Player';
  
  constructor() {
    super('LobbyScene');
  }
  
  init(): void {
    // Load player name from settings
    const settings = GameSettings.getInstance().getSettings();
    this.playerName = settings.playerName || 'Player';
    
    // We'll move the network listener setup to the create method
    // to ensure UI elements exist before event handlers use them
  }
  
  create(): void {
    // Set background
    this.cameras.main.setBackgroundColor('#0a2a3a');
    
    // Create header with title and subtitle
    this.createHeader();
    
    // Add loading text
    this.loadingText = this.add.text(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
      'Connecting to server...',
      {
        fontFamily: 'Arial',
        fontSize: '24px',
        color: '#ffffff',
        align: 'center'
      }
    ).setOrigin(0.5);
    
    // Connection status is now created in createHeader method
    
    // Error text (hidden by default)
    this.errorText = this.add.text(
      this.cameras.main.width / 2,
      this.cameras.main.height - 100,
      '',
      {
        fontFamily: 'Arial',
        fontSize: '18px',
        color: '#ff0000',
        align: 'center'
      }
    ).setOrigin(0.5).setVisible(false);
    
    // Back button to return to main menu - repositioned to top-left corner
    this.backButton = this.add.text(
      30, 
      30, 
      '< Back to Menu', 
      this.buttonStyle
    )
    .setInteractive({ useHandCursor: true })
    .on('pointerdown', () => {
      this.scene.start('MainMenuScene');
    })
    .on('pointerover', () => this.backButton.setStyle({ color: '#ff0' }))
    .on('pointerout', () => this.backButton.setStyle({ color: '#fff' }));
    
    // Refresh button - repositioned to top-right corner
    this.refreshButton = this.add.text(
      this.cameras.main.width - 30,
      30,
      'Refresh',
      this.buttonStyle
    )
    .setOrigin(1, 0) // Right-aligned
    .setInteractive({ useHandCursor: true })
    .on('pointerdown', () => {
      this.refreshRoomsList();
    })
    .on('pointerover', () => this.refreshButton.setStyle({ color: '#ff0' }))
    .on('pointerout', () => this.refreshButton.setStyle({ color: '#fff' }));
    
    // Create the room creation panel (initially hidden)
    this.createCreateRoomPanel();
    
    // Add attribution link
    this.createAttributionLink();
    
    // Set up network event listeners after UI elements are created
    this.setupNetworkListeners();
    
    // Connect to the server
    if (!this.networkService.isConnected()) {
      console.log('Connecting to server from LobbyScene');
      this.networkService.connect();
    } else {
      console.log('Already connected to server');
      this.refreshRoomsList();
    }
    
    // Resize handler
    this.scale.on('resize', this.handleResize, this);
    
    // Also listen for custom window resize event which has better error handling
    this.events.on('windowResized', this.handleWindowResize, this);
    
    // Initial layout
    this.handleResize();
  }
  
  private createHeader(): void {
    const { width } = this.scale;
    
    // Main title with larger text
    this.titleText = this.add.text(
      width / 2, 
      30, 
      'CYTOPOD', 
      { 
        fontFamily: 'Arial', 
        fontSize: '36px', 
        color: '#ffffff',
        align: 'center',
        fontStyle: 'bold'
      }
    ).setOrigin(0.5);
    
    // Subtitle with smaller text
    this.subtitleText = this.add.text(
      width / 2, 
      70, 
      'Multiplayer Lobby', 
      { 
        fontFamily: 'Arial', 
        fontSize: '24px', 
        color: '#ffffff',
        align: 'center'
      }
    ).setOrigin(0.5);
    
    // Connection status - moved to right below the subtitle with 15px spacing
    this.connectionStatus = this.add.text(
      width / 2,
      this.subtitleText.y + 35, // 15px below subtitle
      this.networkService.isConnected() ? 'Connected' : 'Disconnected',
      {
        fontFamily: 'Arial',
        fontSize: '18px',
        color: this.networkService.isConnected() ? '#00ff00' : '#ff0000',
        align: 'center'
      }
    ).setOrigin(0.5);
  }
  
  private setupNetworkListeners(): void {
    // Handle connection event
    this.networkService.onConnect(() => {
      console.log('Connected to server in LobbyScene');
      if (this.connectionStatus) {
        this.connectionStatus.setText('Connected');
        this.connectionStatus.setColor('#00ff00');
      }
      if (this.loadingText) {
        this.loadingText.setVisible(false);
      }
      this.refreshRoomsList();
    });
    
    // Handle disconnection
    this.networkService.onDisconnect(() => {
      console.log('Disconnected from server in LobbyScene');
      if (this.connectionStatus) {
        this.connectionStatus.setText('Disconnected');
        this.connectionStatus.setColor('#ff0000');
      }
      if (this.loadingText) {
        this.loadingText.setText('Connection lost. Please refresh the page.');
        this.loadingText.setVisible(true);
      }
      this.clearRoomsList();
    });
    
    // Handle rooms list
    this.networkService.onRoomsList((rooms) => {
      console.log('Received rooms list:', rooms);
      
      // Ensure roomsList is always an array
      if (!Array.isArray(rooms)) {
        console.warn('Server returned invalid rooms list format:', rooms);
        this.roomsList = [];
      } else {
        this.roomsList = rooms;
      }
      
      console.log('Updated roomsList:', this.roomsList);
      this.updateRoomsDisplay();
      
      if (this.loadingText) {
        this.loadingText.setVisible(false);
      }
    });
    
    // Handle room creation
    this.networkService.onRoomCreated((data) => {
      console.log('Room created:', data);
      if (data && data.id) {
        this.joinRoom(data.id);
      }
    });
    
    // Handle errors
    this.networkService.onError((error) => {
      console.error('Error:', error.message);
      if (error && error.message) {
        this.showError(error.message);
      }
    });
  }
  
  private createCreateRoomPanel(): void {
    // Container for the panel
    this.createRoomPanel = this.add.container(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2 - 50 // Move the panel higher on the screen
    );
    
    // Set a higher depth to ensure it appears above other UI elements
    this.createRoomPanel.setDepth(100);
    
    // Background - increased height to fit all elements properly
    const bg = this.add.rectangle(0, 0, 420, 400, 0x333333, 0.9) // Increase height
      .setOrigin(0.5)
      .setStrokeStyle(2, 0xffffff);
    
    // Panel title
    const title = this.add.text(
      0, 
      -160, // Adjust position
      'Create New Room', 
      { 
        fontFamily: 'Arial', 
        fontSize: '24px', 
        color: '#ffffff', 
        align: 'center' 
      }
    ).setOrigin(0.5);
    
    // Player name label
    const playerNameLabel = this.add.text(
      -180,
      -120, // Position for player name
      'Your Name:',
      {
        fontFamily: 'Arial',
        fontSize: '16px',
        color: '#ffffff'
      }
    ).setOrigin(0, 0.5);
    
    // Player name input (HTML element)
    const playerNameInputElement = document.createElement('input');
    playerNameInputElement.type = 'text';
    playerNameInputElement.id = 'player-name-input';
    playerNameInputElement.name = 'input';
    playerNameInputElement.placeholder = 'Enter your name';
    playerNameInputElement.style.padding = '10px';
    playerNameInputElement.style.width = '300px';
    playerNameInputElement.style.marginBottom = '10px';
    playerNameInputElement.style.borderRadius = '4px';
    playerNameInputElement.style.border = '1px solid #666';
    playerNameInputElement.value = this.playerName;
    
    this.playerNameInput = this.add.dom(-180, -80, playerNameInputElement)
      .setOrigin(0, 0.5);
    
    // Room name label
    const nameLabel = this.add.text(
      -180,
      -40,
      'Room Name:',
      {
        fontFamily: 'Arial',
        fontSize: '16px',
        color: '#ffffff'
      }
    ).setOrigin(0, 0.5);
    
    // Room name input (HTML element)
    const roomNameInputElement = document.createElement('input');
    roomNameInputElement.type = 'text';
    roomNameInputElement.id = 'room-name-input';
    roomNameInputElement.name = 'input';
    roomNameInputElement.placeholder = 'Room Name';
    roomNameInputElement.style.padding = '10px';
    roomNameInputElement.style.width = '300px';
    roomNameInputElement.style.marginBottom = '10px';
    roomNameInputElement.style.borderRadius = '4px';
    roomNameInputElement.style.border = '1px solid #666';
    roomNameInputElement.value = 'My Room';
    
    this.roomNameInput = this.add.dom(-180, 0, roomNameInputElement)
      .setOrigin(0, 0.5);
    
    // Max players label
    const playersLabel = this.add.text(
      -180,
      40,
      'Max Players:',
      {
        fontFamily: 'Arial',
        fontSize: '16px',
        color: '#ffffff'
      }
    ).setOrigin(0, 0.5);
    
    // Max players input (HTML element)
    const playersInput = document.createElement('input');
    playersInput.type = 'number';
    playersInput.id = 'max-players-input';
    playersInput.name = 'input';
    playersInput.placeholder = 'Max Players (2-8)';
    playersInput.min = '2';
    playersInput.max = '8';
    playersInput.value = '4';
    playersInput.style.padding = '10px';
    playersInput.style.width = '300px';
    playersInput.style.marginBottom = '10px';
    playersInput.style.borderRadius = '4px';
    playersInput.style.border = '1px solid #666';
    
    this.maxPlayersInput = this.add.dom(-180, 80, playersInput)
      .setOrigin(0, 0.5);
    
    // Create a container for the buttons at the bottom of the panel
    const buttonContainer = this.add.container(0, 150); // Moved down to fit new input
    
    // Create a modified button style with smaller fixed width for the modal buttons
    const modalButtonStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      ...this.buttonStyle,
      fixedWidth: 140 // Smaller width to fit side by side
    };
    
    // Create button - positioned on the left side
    const createButton = this.add.text(
      -85, 
      0, 
      'Create', 
      modalButtonStyle
    )
    .setOrigin(0.5)
    .setInteractive({ useHandCursor: true })
    .on('pointerdown', () => {
      this.createRoom();
    })
    .on('pointerover', () => createButton.setStyle({ ...modalButtonStyle, color: '#ff0' }))
    .on('pointerout', () => createButton.setStyle({ ...modalButtonStyle, color: '#fff' }));
    
    // Cancel button - positioned on the right side
    const cancelButton = this.add.text(
      85, 
      0, 
      'Cancel', 
      modalButtonStyle
    )
    .setOrigin(0.5)
    .setInteractive({ useHandCursor: true })
    .on('pointerdown', () => {
      this.createRoomPanel.setVisible(false);
    })
    .on('pointerover', () => cancelButton.setStyle({ ...modalButtonStyle, color: '#ff0' }))
    .on('pointerout', () => cancelButton.setStyle({ ...modalButtonStyle, color: '#fff' }));
    
    // Add buttons to the container
    buttonContainer.add([createButton, cancelButton]);
    
    // Add all elements to the panel container
    this.createRoomPanel.add([
      bg, 
      title,
      playerNameLabel,
      this.playerNameInput,
      nameLabel,
      this.roomNameInput,
      playersLabel, 
      this.maxPlayersInput,
      buttonContainer
    ]);
    
    // Hide initially
    this.createRoomPanel.setVisible(false);
  }
  
  private refreshRoomsList(): void {
    if (this.loadingText) {
      this.loadingText.setText('Loading rooms...');
      this.loadingText.setVisible(true);
    }
    this.clearRoomsList();
    this.networkService.getRooms();
  }
  
  private clearRoomsList(): void {
    // Remove all room buttons
    this.roomButtons.forEach(button => button.destroy());
    this.roomButtons = [];
  }
  
  private updateRoomsDisplay(): void {
    this.clearRoomsList();
    
    // Initialize roomsList if it's undefined
    if (!this.roomsList) {
      this.roomsList = [];
    }
    
    // Start Y position higher up on the screen (below header and connection status)
    const startY = 150;
    
    if (this.roomsList.length === 0) {
      // No rooms available - positioned higher on the screen
      const noRoomsText = this.add.text(
        this.cameras.main.width / 2,
        startY + 80, // Move up from center
        'No game rooms available.',
        {
          fontFamily: 'Arial',
          fontSize: '20px',
          color: '#ffffff',
          align: 'center'
        }
      ).setOrigin(0.5);
      
      // Create New Room button - with 100px space after the message
      const createButtonY = noRoomsText.y + 100;
      this.createRoomButton = this.add.text(
        this.cameras.main.width / 2, 
        createButtonY, 
        'Create New Room', 
        this.buttonStyle
      )
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        this.showCreateRoomPanel();
      })
      .on('pointerover', () => {
        if (this.createRoomButton) {
          this.createRoomButton.setStyle({ color: '#ff0' });
        }
      })
      .on('pointerout', () => {
        if (this.createRoomButton) {
          this.createRoomButton.setStyle({ color: '#fff' });
        }
      });
      
      // Help text - with 10px space after the button
      const helpText = this.add.text(
        this.cameras.main.width / 2,
        createButtonY + this.createRoomButton.height + 10, 
        'Create a new room to start playing!',
        {
          fontFamily: 'Arial',
          fontSize: '18px',
          color: '#ffffff',
          align: 'center'
        }
      ).setOrigin(0.5);
      
      const roomContainer = this.add.container(0, 0, [noRoomsText, this.createRoomButton, helpText]);
      this.roomButtons.push(roomContainer);
      
      // No need for the general Create Room button since we already added it for the empty state
      return;
    } else {
      // Create buttons for each room
      const spacing = 70; // Increased spacing between room entries
      
      this.roomsList.forEach((room, index) => {
        const y = startY + (index * spacing);
        
        // Room container
        const roomContainer = this.add.container(this.cameras.main.width / 2, y);
        
        // Room button background
        const bg = this.add.rectangle(0, 0, 500, 50, 0x1a6dd7)
          .setOrigin(0.5)
          .setInteractive({ useHandCursor: true })
          .on('pointerdown', () => {
            this.joinRoom(room.id);
          })
          .on('pointerover', () => bg.setFillStyle(0x3a8df7))
          .on('pointerout', () => bg.setFillStyle(0x1a6dd7));
        
        // Room name
        const nameText = this.add.text(
          -230, 
          0, 
          room.name, 
          { 
            fontFamily: 'Arial', 
            fontSize: '18px', 
            color: '#ffffff' 
          }
        ).setOrigin(0, 0.5);
        
        // Players count
        const playersText = this.add.text(
          170, 
          0, 
          `Players: ${room.players}/${room.maxPlayers}`, 
          { 
            fontFamily: 'Arial', 
            fontSize: '16px', 
            color: '#ffffff' 
          }
        ).setOrigin(0, 0.5);
        
        roomContainer.add([bg, nameText, playersText]);
        this.roomButtons.push(roomContainer);
      });
    }
    
    // Add Create Room button at the bottom with increased spacing
    try {
      const createY = startY + (this.roomsList.length * 70) + 80; // Increased vertical spacing
      
      this.createRoomButton = this.add.text(
        this.cameras.main.width / 2, 
        createY, 
        'Create New Room', 
        this.buttonStyle
      )
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        this.showCreateRoomPanel();
      })
      .on('pointerover', () => {
        if (this.createRoomButton) {
          this.createRoomButton.setStyle({ color: '#ff0' });
        }
      })
      .on('pointerout', () => {
        if (this.createRoomButton) {
          this.createRoomButton.setStyle({ color: '#fff' });
        }
      });
      
      const createButtonContainer = this.add.container(0, 0, [this.createRoomButton]);
      this.roomButtons.push(createButtonContainer);
    } catch (error) {
      console.warn('Error creating room button:', error);
    }
  }
  
  private joinRoom(roomId: string): void {
    if (!roomId) {
      console.error('Attempted to join room with invalid ID');
      this.showError('Invalid room ID');
      return;
    }
    
    // Get player name
    this.playerName = this.getPlayerNameValue();
    
    if (this.loadingText) {
      this.loadingText.setText('Joining room...');
      this.loadingText.setVisible(true);
    }
    
    this.networkService.joinRoom(roomId, this.playerName);
    
    // Remove attribution link before transitioning to multiplayer game
    const attributionLink = document.getElementById('attribution-link');
    if (attributionLink) {
      attributionLink.remove();
      console.log('Removed attribution link before joining multiplayer game');
    }
    
    // Launch the multiplayer game scene with both room ID and player name
    this.scene.start('MultiplayerGameScene', { 
      roomId,
      playerName: this.playerName 
    });
  }
  
  private showCreateRoomPanel(): void {
    this.createRoomPanel.setVisible(true);
  }
  
  private createRoom(): void {
    console.log('createRoom method called');
    
    try {
      // Check connection status
      const isConnected = this.networkService.isConnected();
      console.log('Network connection status:', isConnected);
      
      if (!isConnected) {
        this.showError('Not connected to server. Please wait or refresh the page.');
        console.error('Attempted to create room while disconnected');
        return;
      }
      
      // Get input values using our helper methods
      this.playerName = this.getPlayerNameValue();
      const roomName = this.getRoomNameValue();
      const maxPlayers = this.getMaxPlayersValue();
      
      console.log('Input values:', { playerName: this.playerName, roomName, maxPlayers });
      
      // Validate player name
      if (!this.playerName) {
        this.showError('Please enter your name');
        return;
      }
      
      // Validate room name
      if (!roomName) {
        this.showError('Please enter a room name');
        return;
      }
      
      if (isNaN(maxPlayers) || maxPlayers < 2 || maxPlayers > 8) {
        this.showError('Please enter a valid number of players (2-8)');
        return;
      }
      
      // Create the room
      if (this.loadingText) {
        this.loadingText.setText('Creating room...');
        this.loadingText.setVisible(true);
      }
      
      if (this.createRoomPanel) {
        this.createRoomPanel.setVisible(false);
      }
      
      console.log('Creating room with name:', roomName, 'and max players:', maxPlayers);
      console.log('Socket ID:', this.networkService.getSocketId());
      this.networkService.createRoom(roomName, maxPlayers);
      
      // For additional debugging - mark that we've sent the create room request
      console.log('Create room request sent to server at:', new Date().toISOString());
    } catch (error) {
      console.error('Error in createRoom:', error);
      this.showError('An error occurred while creating the room. Please try again.');
    }
  }
  
  private showError(message: string): void {
    if (!this.errorText) {
      console.error('Error:', message);
      return;
    }
    
    this.errorText.setText(message);
    this.errorText.setVisible(true);
    
    // Auto-hide after 5 seconds
    this.time.delayedCall(5000, () => {
      if (this.errorText) {
        this.errorText.setVisible(false);
      }
    });
  }
  
  private handleResize(): void {
    // Only proceed if the scene is active
    if (!this.scene.isActive('LobbyScene')) {
      return;
    }
    
    const { width, height } = this.scale;
    
    // Reposition title and subtitle
    if (this.titleText) {
      this.titleText.setPosition(width / 2, 30);
    }
    if (this.subtitleText) {
      this.subtitleText.setPosition(width / 2, 70);
    }
    
    // Reposition connection status below subtitle
    if (this.connectionStatus) {
      this.connectionStatus.setPosition(width / 2, this.subtitleText ? this.subtitleText.y + 35 : 105);
    }
    
    // Reposition loading text
    if (this.loadingText) {
      this.loadingText.setPosition(width / 2, height / 2);
    }
    
    // Reposition buttons
    if (this.backButton) {
      this.backButton.setPosition(30, 30);
    }
    if (this.refreshButton) {
      this.refreshButton.setPosition(width - 30, 30);
      this.refreshButton.setOrigin(1, 0); // Right-aligned
    }
    
    // Reposition error text
    if (this.errorText) {
      this.errorText.setPosition(width / 2, height - 100);
    }
    
    // Reposition create room panel - keep it higher on the screen
    if (this.createRoomPanel) {
      this.createRoomPanel.setPosition(width / 2, height / 2 - 50);
    }
    
    // Update room list positions - only if the scene is fully initialized
    try {
      this.updateRoomsDisplay();
    } catch (error) {
      console.warn('Error updating rooms display during resize:', error);
    }
  }
  
  private handleWindowResize(size: { width: number, height: number }): void {
    console.log(`LobbyScene handling window resize event: ${size.width}x${size.height}`);
    
    try {
      // Only call the resize handler if we're fully initialized
      if (this.connectionStatus && this.loadingText && this.titleText) {
        this.handleResize();
      } else {
        console.log('LobbyScene not fully initialized yet, skipping resize handling');
      }
    } catch (error) {
      console.error('Error in LobbyScene handleWindowResize:', error);
    }
  }
  
  update(): void {
    // Update connection status color based on actual connection state
    if (this.connectionStatus) {
      if (this.networkService.isConnected()) {
        this.connectionStatus.setText('Connected');
        this.connectionStatus.setColor('#00ff00');
      } else {
        this.connectionStatus.setText('Disconnected');
        this.connectionStatus.setColor('#ff0000');
      }
    }
  }
  
  shutdown(): void {
    console.log('LobbyScene shutdown called');
    
    // Remove UI elements
    this.roomButtons.forEach(button => button.destroy());
    this.roomButtons = [];
    
    // Remove event listeners
    this.events.off('windowResized', this.handleWindowResize);
    this.scale.off('resize', this.handleResize);
    
    // We keep the network connection alive because MultiplayerGameScene will need it
    // The connection will be cleaned up when the game ends or when returning to main menu
    
    // Remove the attribution link when leaving the scene
    const attributionLink = document.getElementById('attribution-link');
    if (attributionLink) {
      attributionLink.remove();
      console.log('Removed attribution link in LobbyScene shutdown');
    }
    
    console.log('LobbyScene shutdown complete');
  }
  
  /**
   * Get the room name input value directly from DOM
   */
  private getRoomNameValue(): string {
    try {
      // Try using Phaser's DOM element first
      if (this.roomNameInput) {
        const inputElement = this.roomNameInput.getChildByName('input') as HTMLInputElement;
        if (inputElement && inputElement.value) {
          console.log('Got room name from Phaser DOM element:', inputElement.value);
          return inputElement.value.trim();
        }
      }
      
      // Fallback to direct DOM query
      const inputElement = document.getElementById('room-name-input') as HTMLInputElement;
      if (inputElement && inputElement.value) {
        console.log('Got room name from direct DOM query:', inputElement.value);
        return inputElement.value.trim();
      }
      
      console.warn('Could not find room name input element');
      return '';
    } catch (error) {
      console.error('Error getting room name:', error);
      return '';
    }
  }
  
  /**
   * Get the max players input value directly from DOM
   */
  private getMaxPlayersValue(): number {
    try {
      // Try using Phaser's DOM element first
      if (this.maxPlayersInput) {
        const inputElement = this.maxPlayersInput.getChildByName('input') as HTMLInputElement;
        if (inputElement && inputElement.value) {
          console.log('Got max players from Phaser DOM element:', inputElement.value);
          return parseInt(inputElement.value);
        }
      }
      
      // Fallback to direct DOM query
      const inputElement = document.getElementById('max-players-input') as HTMLInputElement;
      if (inputElement && inputElement.value) {
        console.log('Got max players from direct DOM query:', inputElement.value);
        return parseInt(inputElement.value);
      }
      
      console.warn('Could not find max players input element');
      return 4; // Default value
    } catch (error) {
      console.error('Error getting max players:', error);
      return 4; // Default value
    }
  }
  
  /**
   * Get the player name input value directly from DOM
   */
  private getPlayerNameValue(): string {
    try {
      let playerName = 'Player';
      
      // Try using Phaser's DOM element first
      if (this.playerNameInput) {
        const inputElement = this.playerNameInput.getChildByName('input') as HTMLInputElement;
        if (inputElement && inputElement.value) {
          console.log('Got player name from Phaser DOM element:', inputElement.value);
          playerName = inputElement.value.trim();
        }
      }
      
      // Fallback to direct DOM query
      if (playerName === 'Player') {
        const inputElement = document.getElementById('player-name-input') as HTMLInputElement;
        if (inputElement && inputElement.value) {
          console.log('Got player name from direct DOM query:', inputElement.value);
          playerName = inputElement.value.trim();
        }
      }
      
      if (playerName === '') {
        playerName = 'Player';
      }
      
      // Save to settings for persistence
      const settingsManager = GameSettings.getInstance();
      settingsManager.updateSetting('playerName', playerName);
      settingsManager.saveSettings();
      console.log('Saved player name to settings:', playerName);
      
      return playerName;
    } catch (error) {
      console.error('Error getting player name:', error);
      return 'Player'; // Default value
    }
  }
  
  /**
   * Creates an attribution link in the bottom right corner
   */
  private createAttributionLink(): void {
    const attribution = document.createElement('a');
    attribution.href = 'https://x.com/illyakonovalov';
    attribution.target = '_blank';
    attribution.textContent = 'by @illyakonovalov';
    attribution.style.fontFamily = 'system-ui, sans-serif';
    attribution.style.position = 'fixed';
    attribution.style.bottom = '-1px';
    attribution.style.left = '-1px';
    attribution.style.padding = '7px';
    attribution.style.fontSize = '14px';
    attribution.style.fontWeight = 'bold';
    attribution.style.background = '#fff';
    attribution.style.color = '#000';
    attribution.style.textDecoration = 'none';
    attribution.style.borderTopRightRadius = '12px';
    attribution.style.zIndex = '10000';
    attribution.style.border = '1px solid #fff';
    
    // If there's already an attribution link, remove it first
    const existingAttribution = document.getElementById('attribution-link');
    if (existingAttribution) {
      existingAttribution.remove();
    }
    
    // Add ID for easy reference
    attribution.id = 'attribution-link';
    
    // Add to DOM
    document.body.appendChild(attribution);
  }
} 